import type { User } from 'firebase/auth'
import {
  doc,
  getDoc,
  onSnapshot,
  serverTimestamp,
  setDoc,
  type Unsubscribe,
} from 'firebase/firestore'
import { db } from '../lib/firebase'

export const PLAYER_NAME_MIN_LENGTH = 2
export const PLAYER_NAME_MAX_LENGTH = 24
export const LOCAL_PLAYER_NAME_KEY = 'ybg.player.local-name'

export const playerAvatars = [
  { id: 'arcade-comet', label: 'Arcade Comet', glyph: '✦', colors: ['#caff3d', '#527c00'] },
  { id: 'violet-knight', label: 'Violet Knight', glyph: '◆', colors: ['#b79aff', '#5c2fd0'] },
  { id: 'pixel-phantom', label: 'Pixel Phantom', glyph: '◈', colors: ['#6ce7ff', '#11647f'] },
  { id: 'rose-striker', label: 'Rose Striker', glyph: '✧', colors: ['#ff8fc6', '#a61c62'] },
] as const

export type PlayerAvatarId = (typeof playerAvatars)[number]['id']

export type PlayerProfile = {
  uid: string
  displayName: string
  photoURL: string | null
  avatarId: PlayerAvatarId | null
  isAnonymous: boolean
}

export type PlayerProfileInput = Pick<PlayerProfile, 'displayName' | 'photoURL' | 'avatarId'>

export type PlayerIdentity = Pick<PlayerProfile, 'displayName' | 'photoURL' | 'avatarId'>

type PlayerAccount = Pick<User, 'uid' | 'displayName' | 'photoURL' | 'isAnonymous'>

export function isPlayerAvatarId(value: unknown): value is PlayerAvatarId {
  return playerAvatars.some((avatar) => avatar.id === value)
}

export function normalizePlayerName(value: string) {
  return value.trim().replace(/\s+/g, ' ')
}

export function playerNameError(value: string) {
  const normalized = normalizePlayerName(value)
  if (normalized.length < PLAYER_NAME_MIN_LENGTH) return `Use at least ${PLAYER_NAME_MIN_LENGTH} characters.`
  if (normalized.length > PLAYER_NAME_MAX_LENGTH) return `Keep it under ${PLAYER_NAME_MAX_LENGTH} characters.`
  if ([...normalized].some((character) => character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127)) return 'That name contains unsupported characters.'
  return ''
}

export function defaultPlayerName(user: PlayerAccount) {
  const accountName = normalizePlayerName(user.displayName || '')
  if (!user.isAnonymous && !playerNameError(accountName)) return accountName
  return `Player ${user.uid.slice(0, 4).toUpperCase()}`
}

export function loadLocalPlayerName(storage?: Pick<Storage, 'getItem'>) {
  const normalized = normalizePlayerName(storage?.getItem(LOCAL_PLAYER_NAME_KEY) ?? '')
  return playerNameError(normalized) ? '' : normalized
}

export function saveLocalPlayerName(value: string, storage?: Pick<Storage, 'setItem'>) {
  const normalized = normalizePlayerName(value)
  if (playerNameError(normalized)) return false
  storage?.setItem(LOCAL_PLAYER_NAME_KEY, normalized)
  return true
}

export function hasChosenPlayerName(user: PlayerAccount, profile: PlayerProfile | null) {
  if (!user.isAnonymous) return true
  return Boolean(profile && normalizePlayerName(profile.displayName) !== defaultPlayerName(user))
}

export function playerIdentity(user: PlayerAccount, profile: PlayerProfile | null): PlayerIdentity {
  return {
    displayName: profile?.displayName || defaultPlayerName(user),
    photoURL: profile ? profile.photoURL : user.photoURL || null,
    avatarId: profile?.avatarId || null,
  }
}

function profileFromData(user: PlayerAccount, data: Record<string, unknown>): PlayerProfile {
  const savedName = typeof data.displayName === 'string' ? normalizePlayerName(data.displayName) : ''
  return {
    uid: user.uid,
    displayName: playerNameError(savedName) ? defaultPlayerName(user) : savedName,
    photoURL: typeof data.photoURL === 'string' ? data.photoURL : null,
    avatarId: isPlayerAvatarId(data.avatarId) ? data.avatarId : null,
    isAnonymous: user.isAnonymous,
  }
}

function requireDatabase() {
  if (!db) throw new Error('Player profiles are not configured yet.')
  return db
}

export async function ensurePlayerProfile(user: User) {
  const firestore = requireDatabase()
  const profileRef = doc(firestore, 'players', user.uid)
  const snapshot = await getDoc(profileRef)

  if (snapshot.exists()) {
    const savedName = typeof snapshot.data().displayName === 'string' ? snapshot.data().displayName : ''
    const avatarId = isPlayerAvatarId(snapshot.data().avatarId) ? snapshot.data().avatarId : null
    await setDoc(profileRef, {
      displayName: playerNameError(savedName) ? defaultPlayerName(user) : normalizePlayerName(savedName),
      photoURL: avatarId || !snapshot.data().photoURL ? null : user.photoURL || null,
      avatarId,
      createdAt: snapshot.data().createdAt || serverTimestamp(),
      isAnonymous: user.isAnonymous,
      lastSeenAt: serverTimestamp(),
    }, { merge: true })
    return
  }

  await setDoc(profileRef, {
    displayName: defaultPlayerName(user),
    photoURL: user.photoURL || null,
    avatarId: null,
    isAnonymous: user.isAnonymous,
    createdAt: serverTimestamp(),
    lastSeenAt: serverTimestamp(),
  })
}

export function subscribePlayerProfile(
  user: User,
  onProfile: (profile: PlayerProfile | null) => void,
  onError: (message: string) => void,
): Unsubscribe {
  if (!db) {
    onProfile(null)
    return () => undefined
  }

  return onSnapshot(doc(db, 'players', user.uid), (snapshot) => {
    onProfile(snapshot.exists() ? profileFromData(user, snapshot.data()) : null)
  }, () => onError('Your player profile could not be loaded.'))
}

export async function savePlayerProfile(user: User, input: PlayerProfileInput) {
  const error = playerNameError(input.displayName)
  if (error) throw new Error(error)
  if (input.avatarId && !isPlayerAvatarId(input.avatarId)) throw new Error('Choose a valid avatar.')
  if (input.avatarId && input.photoURL) throw new Error('Choose either an avatar or your account photo.')
  if (input.photoURL && input.photoURL !== user.photoURL) throw new Error('Only your verified account photo can be used.')

  const profileRef = doc(requireDatabase(), 'players', user.uid)
  const snapshot = await getDoc(profileRef)
  await setDoc(profileRef, {
    displayName: normalizePlayerName(input.displayName),
    photoURL: input.photoURL || null,
    avatarId: input.avatarId || null,
    isAnonymous: user.isAnonymous,
    createdAt: snapshot.data()?.createdAt || serverTimestamp(),
    lastSeenAt: serverTimestamp(),
  }, { merge: true })
}
